/**
 * MoveNet Lightning pose detector on ONNX Runtime Web.
 *
 * Uses a custom export of https://github.com/lee-man/movenet-pytorch that emits
 * only the four raw prediction heads (person heatmap, kpt regression, kpt
 * heatmap, kpt offset) instead of the built-in argmax/gather postprocessing.
 * That avoids ORT WebGPU's GatherND kernel bug (uniform variable mismatch)
 * that blocks the tf2onnx-exported MoveNet. CenterNet decoding runs in JS
 * on the tiny 48×48 heatmaps below.
 *
 * MoveNet-compatible output: [{keypoints: [{x, y, score, name}, ...], score}].
 */

import * as ort from 'onnxruntime-web/all';

ort.env.wasm.wasmPaths = '/ort/';
if (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) {
  ort.env.wasm.numThreads = Math.min(navigator.hardwareConcurrency, 8);
}
if (ort.env.webgpu) {
  ort.env.webgpu.powerPreference = 'high-performance';
}

const KEYPOINT_NAMES = [
  'nose', 'left_eye', 'right_eye', 'left_ear', 'right_ear',
  'left_shoulder', 'right_shoulder', 'left_elbow', 'right_elbow',
  'left_wrist', 'right_wrist', 'left_hip', 'right_hip',
  'left_knee', 'right_knee', 'left_ankle', 'right_ankle'
];

const NUM_KEYPOINTS = 17;
const DEFAULT_INPUT_SIZE = 192;   // MoveNet Lightning
const DEFAULT_FT_SIZE = 48;        // Feature map dimension (input / 4)

function sigmoid(x) { return 1 / (1 + Math.exp(-x)); }

export class OnnxMoveNetDetector {
  constructor(modelPath, options = {}) {
    this.modelPath = modelPath;
    this.inputSize = options.inputSize || DEFAULT_INPUT_SIZE;
    this.ftSize = options.ftSize || DEFAULT_FT_SIZE;
    this.confThreshold = options.confThreshold || 0.25;

    this.session = null;
    this.isInitialized = false;

    this.modelInfo = { modelName: null, deployedAt: null };

    this._canvas = null;
    this._ctx = null;
    this._inputBuffer = null;
    this._lastScale = null;

    // Precomputed decode helpers
    this._weightToCenter = null;  // [ft*ft]
  }

  async initialize(timeout = 30000) {
    if (this.isInitialized) return true;
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`MoveNet init timed out after ${timeout}ms`)), timeout);
    });
    await Promise.race([this._doInitialize(), timeoutPromise]);
    return true;
  }

  async _doInitialize() {
    const modelDir = this.modelPath.replace(/\/[^/]+$/, '');
    let actualModelUrl = this.modelPath;

    try {
      const resp = await fetch(`${modelDir}/manifest.movenet.onnx.json?_=${Date.now()}`, { cache: 'no-store' });
      if (resp.ok) {
        const manifest = await resp.json();
        if (manifest.onnxModel) {
          actualModelUrl = `${modelDir}/${manifest.onnxModel}`;
          this.modelInfo = { modelName: manifest.modelName || null, deployedAt: manifest.deployedAt || null };
          if (manifest.inputSize) this.inputSize = manifest.inputSize;
          if (manifest.ftSize) this.ftSize = manifest.ftSize;
        }
      }
    } catch (_e) { /* fall back to modelPath */ }

    console.log('[OnnxMoveNet] Loading', actualModelUrl);
    this._modelUrl = actualModelUrl;

    const params = typeof location !== 'undefined'
      ? new URLSearchParams(location.search) : new URLSearchParams();
    const backend = params.get('backend') || params.get('pose_backend') || 'webgpu';
    const eps = backend === 'wasm' ? ['wasm'] : [{ name: 'webgpu' }, 'wasm'];

    this.session = await ort.InferenceSession.create(this._modelUrl, {
      executionProviders: eps,
      graphOptimizationLevel: 'all',
      enableCpuMemArena: true,
      enableMemPattern: true
    });
    console.log('[OnnxMoveNet] Session created (', backend, ')');

    this._canvas = document.createElement('canvas');
    this._canvas.width = this.inputSize;
    this._canvas.height = this.inputSize;
    this._ctx = this._canvas.getContext('2d');

    // NHWC float32 tensor buffer
    this._inputBuffer = new Float32Array(this.inputSize * this.inputSize * 3);

    // Precompute distance-to-center weights (used to pick person center)
    const ft = this.ftSize;
    const half = ft / 2.0;
    this._weightToCenter = new Float32Array(ft * ft);
    for (let y = 0; y < ft; y++) {
      for (let x = 0; x < ft; x++) {
        const dy = y - half, dx = x - half;
        this._weightToCenter[y * ft + x] = 1.0 / (Math.sqrt(dy * dy + dx * dx) + 1.8);
      }
    }

    this.isInitialized = true;
    return true;
  }

  /**
   * Preprocess: letterbox video to input canvas → float32 NHWC tensor in [0, 255].
   * The exported model does `x/127.5 - 1.0` internally.
   */
  preprocess(source) {
    const srcWidth = source.videoWidth || source.width;
    const srcHeight = source.videoHeight || source.height;

    const scale = Math.min(this.inputSize / srcWidth, this.inputSize / srcHeight);
    const newWidth = Math.round(srcWidth * scale);
    const newHeight = Math.round(srcHeight * scale);
    const offsetX = Math.floor((this.inputSize - newWidth) / 2);
    const offsetY = Math.floor((this.inputSize - newHeight) / 2);
    this._lastScale = { scale, offsetX, offsetY, srcWidth, srcHeight };

    this._ctx.fillStyle = 'rgb(0,0,0)';
    this._ctx.fillRect(0, 0, this.inputSize, this.inputSize);
    this._ctx.drawImage(source, offsetX, offsetY, newWidth, newHeight);

    const imageData = this._ctx.getImageData(0, 0, this.inputSize, this.inputSize);
    const pixels = imageData.data;
    const buf = this._inputBuffer;
    for (let i = 0, j = 0; i < pixels.length; i += 4, j += 3) {
      buf[j]     = pixels[i];
      buf[j + 1] = pixels[i + 1];
      buf[j + 2] = pixels[i + 2];
    }
    return new ort.Tensor('float32', buf, [1, this.inputSize, this.inputSize, 3]);
  }

  /**
   * CenterNet-style keypoint decoding, ported from lee-man/movenet-pytorch.
   * All 4 tensors are NCHW from the PyTorch export:
   *   hm       [1, 1, ft, ft]   person center heatmap (raw logits, we sigmoid here)
   *   hps      [1, 34, ft, ft]  kpt regress from center (34 = 17*2, order y0,x0,y1,x1,...)
   *   hm_hp    [1, 17, ft, ft]  kpt heatmaps (raw logits, we sigmoid here)
   *   hp_offset[1, 34, ft, ft]  sub-pixel offsets at each kpt
   */
  decode(hm, hps, hm_hp, hp_offset) {
    const ft = this.ftSize;
    const ft2 = ft * ft;

    // 1) Person center: argmax(sigmoid(hm) * weight_to_center)
    let bestIdx = 0, bestScore = -Infinity;
    for (let i = 0; i < ft2; i++) {
      const s = sigmoid(hm[i]) * this._weightToCenter[i];
      if (s > bestScore) { bestScore = s; bestIdx = i; }
    }
    const ct_y = Math.floor(bestIdx / ft);
    const ct_x = bestIdx - ct_y * ft;

    // 2) Coarse kpt = regress at center + (ct_y, ct_x)
    //    hps layout: NCHW → channel c goes over spatial [ft, ft]
    //    kpt_regress[kpt_k, 0] = hps channel (2k),   kpt_regress[kpt_k, 1] = hps channel (2k+1)
    //    But the pytorch code reshapes as (-1, 17, 2) after moving channel to last dim.
    //    In NCHW, `hps[0, c, y, x]` = hps[c*ft2 + y*ft + x].
    //    permute(1,2,0) makes it [ft, ft, 34] then view(-1,17,2) makes [ft*ft, 17, 2]
    //    with element [i, k, 0/1] = hps_permuted[y=i/ft, x=i%ft, 2*k or 2*k+1]
    //    = hps_channel_(2k or 2k+1)[y, x].
    //    So coarse_kpt[k, 0] = hps[(2k)*ft2 + bestIdx]
    //       coarse_kpt[k, 1] = hps[(2k+1)*ft2 + bestIdx]
    const coarseKpt = new Float32Array(NUM_KEYPOINTS * 2);
    for (let k = 0; k < NUM_KEYPOINTS; k++) {
      coarseKpt[k * 2]     = hps[(2 * k)     * ft2 + bestIdx] + ct_y;
      coarseKpt[k * 2 + 1] = hps[(2 * k + 1) * ft2 + bestIdx] + ct_x;
    }

    // 3) Refine each kpt: argmax over ft×ft of sigmoid(hm_hp[k]) / dist_weight
    //    hm_hp NCHW: kpt k's heatmap at hm_hp[k*ft2 + y*ft + x]
    const kptTopInds = new Int32Array(NUM_KEYPOINTS);
    for (let k = 0; k < NUM_KEYPOINTS; k++) {
      const cyk = coarseKpt[k * 2];
      const cxk = coarseKpt[k * 2 + 1];
      const base = k * ft2;
      let bi = 0, bs = -Infinity;
      for (let y = 0; y < ft; y++) {
        for (let x = 0; x < ft; x++) {
          const dy = y - cyk, dx = x - cxk;
          const distW = Math.sqrt(dy * dy + dx * dx) + 1.8;
          const s = sigmoid(hm_hp[base + y * ft + x]) / distW;
          if (s > bs) { bs = s; bi = y * ft + x; }
        }
      }
      kptTopInds[k] = bi;
    }

    // 4) Final coords using sub-pixel offsets
    const { scale, offsetX: ox, offsetY: oy, srcWidth, srcHeight } = this._lastScale;
    const keypoints = new Array(NUM_KEYPOINTS);
    let totalScore = 0;
    for (let k = 0; k < NUM_KEYPOINTS; k++) {
      const idx = kptTopInds[k];
      const ky = Math.floor(idx / ft);
      const kx = idx - ky * ft;
      const off_y = hp_offset[(2 * k)     * ft2 + idx];
      const off_x = hp_offset[(2 * k + 1) * ft2 + idx];
      const y_norm = (ky + off_y) / ft;   // 0..1 in input image space
      const x_norm = (kx + off_x) / ft;
      const score = sigmoid(hm_hp[k * ft2 + idx]);

      // Denormalize to input canvas → undo letterbox → source pixel coords
      const x_input = x_norm * this.inputSize;
      const y_input = y_norm * this.inputSize;
      const x = Math.max(0, Math.min(srcWidth,  (x_input - ox) / scale));
      const y = Math.max(0, Math.min(srcHeight, (y_input - oy) / scale));

      keypoints[k] = { x, y, score, name: KEYPOINT_NAMES[k] };
      totalScore += score;
    }
    const avgScore = totalScore / NUM_KEYPOINTS;
    if (avgScore < this.confThreshold) return [];
    return [{ keypoints, score: avgScore }];
  }

  async estimatePoses(source) {
    if (!this.isInitialized) throw new Error('[OnnxMoveNet] Not initialized');

    let inputTensor = null;
    let results = null;
    try {
      const t0 = performance.now();
      inputTensor = this.preprocess(source);
      const t1 = performance.now();
      results = await this.session.run({ [this.session.inputNames[0]]: inputTensor });
      const t2 = performance.now();
      const poses = this.decode(
        results.hm.data,
        results.hps.data,
        results.hm_hp.data,
        results.hp_offset.data
      );
      const t3 = performance.now();
      this._frameCount = (this._frameCount || 0) + 1;
      if (this._frameCount % 60 === 1) {
        console.log(`[OnnxMoveNet] prep=${(t1-t0).toFixed(2)}ms infer=${(t2-t1).toFixed(2)}ms decode=${(t3-t2).toFixed(2)}ms`);
      }
      return poses;
    } catch (err) {
      console.error('[OnnxMoveNet] estimatePoses error:', err);
      throw err;
    } finally {
      if (inputTensor) inputTensor.dispose?.();
      if (results) for (const k of Object.keys(results)) results[k]?.dispose?.();
    }
  }

  getModelInfo() { return this.modelInfo; }

  async stop() {
    if (this.session) {
      await this.session.release();
      this.session = null;
    }
    this.isInitialized = false;
  }
}

export default OnnxMoveNetDetector;
